import { useEffect, useMemo, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button } from "@project/ui";
import { Icon, formatShort } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import {
  fetchExpenseCategories,
  fetchExpenses,
  fetchHouseMembers,
  homeExpenseQueryKeys,
  markExpensePaid,
  patchExpense,
} from "@apps/dashboard/lib/home-expense-api";
import { expenseEffectiveMinor } from "@apps/dashboard/lib/expense-effective-amount";
import {
  buildExpenseMonthGroups,
  formatYearMonthTitle,
} from "@apps/dashboard/lib/expense-month-ledger";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import { minorToMajorInputValue, parseAmountToMinor } from "@apps/dashboard/lib/parse-amount";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import type { ExpenseListItem, ExpenseListQuery } from "@apps/dashboard/lib/home-expense-types";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";

export default function ExpensesPage(): JSX.Element {
  const prefersReducedMotion = useReducedMotion();
  const { houseId } = useParams<{ houseId: string }>();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  useSyncHouseFromParams(houseId);

  const qApplied = searchParams.get("q") ?? "";
  const categoryApplied = searchParams.get("categoryId") ?? "";
  const paidApplied = searchParams.get("paid") ?? "";
  const scopeApplied = searchParams.get("scope") ?? "";

  const [draftQ, setDraftQ] = useState(qApplied);
  const [draftCategory, setDraftCategory] = useState(categoryApplied);
  const [draftPaid, setDraftPaid] = useState(paidApplied);
  const [draftScope, setDraftScope] = useState(scopeApplied);

  const [confirmMarkPaidId, setConfirmMarkPaidId] = useState<string | null>(null);
  const [confirmPaidZeroId, setConfirmPaidZeroId] = useState<string | null>(null);
  const [actualModalExpense, setActualModalExpense] = useState<ExpenseListItem | null>(null);
  const [actualDraft, setActualDraft] = useState("");

  useEffect(() => {
    setDraftQ(qApplied);
    setDraftCategory(categoryApplied);
    setDraftPaid(paidApplied);
    setDraftScope(scopeApplied);
  }, [qApplied, categoryApplied, paidApplied, scopeApplied]);

  useEffect(() => {
    if (!actualModalExpense) {
      setActualDraft("");
      return;
    }
    const minor =
      actualModalExpense.actualAmountMinor != null
        ? actualModalExpense.actualAmountMinor
        : actualModalExpense.amountMinor;
    setActualDraft(minorToMajorInputValue(minor));
  }, [actualModalExpense]);

  const listQuery: ExpenseListQuery = useMemo(() => {
    const paid =
      paidApplied === "yes" || paidApplied === "no" ? (paidApplied as "yes" | "no") : undefined;
    const contributionScope =
      scopeApplied === "shared"
        ? "shared"
        : scopeApplied === "personal"
          ? "personal"
          : undefined;
    return {
      sort: "date_desc" as const,
      ...(categoryApplied ? { categoryId: categoryApplied } : {}),
      ...(paid ? { paid } : {}),
      ...(contributionScope ? { contributionScope } : {}),
      ...(qApplied.trim() ? { search: qApplied.trim() } : {}),
    };
  }, [categoryApplied, paidApplied, qApplied, scopeApplied]);

  const expensesQuery = useQuery({
    queryKey:
      houseId != null
        ? homeExpenseQueryKeys.expenses(locale, houseId, listQuery)
        : ["expenses", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchExpenses(locale, houseId, listQuery);
    },
    enabled: Boolean(houseId),
  });

  const membersQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.houseMembers(locale, houseId) : ["mem", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchHouseMembers(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const categoriesQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.categories(locale, houseId) : ["cat", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchExpenseCategories(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const memberIdToName = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of membersQuery.data ?? []) {
      m.set(row.userId, row.user.name);
    }
    return m;
  }, [membersQuery.data]);

  const monthGroups = useMemo(
    () => buildExpenseMonthGroups(expensesQuery.data ?? [], memberIdToName),
    [expensesQuery.data, memberIdToName],
  );

  const markPaidMutation = useMutation({
    mutationFn: (expenseId: string) => {
      if (!houseId) throw new Error("missing houseId");
      return markExpensePaid(locale, houseId, expenseId);
    },
    onSuccess: (detail) => {
      if (!houseId) return;
      void queryClient.invalidateQueries({ queryKey: ["houses", houseId, "expenses"] });
      void queryClient.invalidateQueries({ queryKey: ["houses", houseId, "dashboard"] });
      setConfirmMarkPaidId(null);
      navigate(dashboardPaths.houseExpensePaidFeedback(houseId, detail.id), {
        state: { expense: detail },
      });
    },
  });

  const paidZeroMutation = useMutation({
    mutationFn: async (expenseId: string) => {
      if (!houseId) throw new Error("missing houseId");
      await patchExpense(locale, houseId, expenseId, { actualAmountMinor: 0 });
      return markExpensePaid(locale, houseId, expenseId);
    },
    onSuccess: (detail) => {
      if (!houseId) return;
      void queryClient.invalidateQueries({ queryKey: ["houses", houseId, "expenses"] });
      void queryClient.invalidateQueries({ queryKey: ["houses", houseId, "dashboard"] });
      setConfirmPaidZeroId(null);
      navigate(dashboardPaths.houseExpensePaidFeedback(houseId, detail.id), {
        state: { expense: detail },
      });
    },
  });

  const patchActualMutation = useMutation({
    mutationFn: (args: { expenseId: string; actualAmountMinor: number | null }) => {
      if (!houseId) throw new Error("missing houseId");
      return patchExpense(locale, houseId, args.expenseId, {
        actualAmountMinor: args.actualAmountMinor,
      });
    },
    onSuccess: () => {
      if (!houseId) return;
      void queryClient.invalidateQueries({ queryKey: ["houses", houseId, "expenses"] });
      void queryClient.invalidateQueries({ queryKey: ["houses", houseId, "dashboard"] });
      setActualModalExpense(null);
    },
  });

  const confirmExpense = useMemo(
    () =>
      confirmMarkPaidId
        ? (expensesQuery.data ?? []).find((x) => x.id === confirmMarkPaidId) ?? null
        : null,
    [confirmMarkPaidId, expensesQuery.data],
  );

  const confirmPaidZeroExpense = useMemo(
    () =>
      confirmPaidZeroId
        ? (expensesQuery.data ?? []).find((x) => x.id === confirmPaidZeroId) ?? null
        : null,
    [confirmPaidZeroId, expensesQuery.data],
  );

  if (!houseId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState />
      </div>
    );
  }

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-headline text-headline-sm font-bold text-primary">{t("dashboardUi.pages.expenses")}</h1>
        <Button asChild>
          <Link to={dashboardPaths.houseExpenseNew(houseId)}>{t("dashboardUi.expenses.newCta")}</Link>
        </Button>
      </div>

      <section
        className="mt-6 rounded-2xl border border-outline-variant/15 bg-surface-container-low p-5 shadow-sm"
        aria-labelledby="expense-filters-heading"
      >
        <h2 id="expense-filters-heading" className="font-headline text-sm font-bold text-primary">
          {t("dashboardUi.expenses.filters.title")}
        </h2>
        <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end">
          <label className="min-w-[12rem] flex-1 font-body text-body-md text-on-surface-variant">
            <span className="text-label-md text-on-surface-variant">{t("common.search")}</span>
            <input
              type="search"
              value={draftQ}
              onChange={(e) => setDraftQ(e.target.value)}
              placeholder={t("dashboardUi.expenses.filters.searchPlaceholder")}
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-on-surface"
            />
          </label>
          <label className="min-w-[10rem] font-body text-body-md text-on-surface-variant">
            <span className="text-label-md">{t("dashboardUi.expenses.form.categoryLabel")}</span>
            <select
              value={draftCategory}
              onChange={(e) => setDraftCategory(e.target.value)}
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-on-surface"
            >
              <option value="">{t("dashboardUi.expenses.filters.categoryAll")}</option>
              {(categoriesQuery.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="min-w-[10rem] font-body text-body-md text-on-surface-variant">
            <span className="text-label-md">{t("dashboardUi.expenses.filters.scopeLabel")}</span>
            <select
              value={draftScope}
              onChange={(e) => setDraftScope(e.target.value)}
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-on-surface"
            >
              <option value="">{t("dashboardUi.expenses.filters.scopeAll")}</option>
              <option value="shared">{t("dashboardUi.expenses.filters.scopeShared")}</option>
              <option value="personal">{t("dashboardUi.expenses.filters.scopePersonal")}</option>
            </select>
          </label>
          <label className="min-w-[10rem] font-body text-body-md text-on-surface-variant">
            <span className="text-label-md">{t("dashboardUi.expenses.paid")}</span>
            <select
              value={draftPaid}
              onChange={(e) => setDraftPaid(e.target.value)}
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-on-surface"
            >
              <option value="">{t("dashboardUi.expenses.filters.paidAll")}</option>
              <option value="yes">{t("dashboardUi.expenses.filters.paidYes")}</option>
              <option value="no">{t("dashboardUi.expenses.filters.paidNo")}</option>
            </select>
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              className="rounded-xl"
              onClick={() => {
                const p = new URLSearchParams();
                if (draftQ.trim()) p.set("q", draftQ.trim());
                if (draftCategory) p.set("categoryId", draftCategory);
                if (draftPaid === "yes" || draftPaid === "no") p.set("paid", draftPaid);
                if (draftScope === "shared" || draftScope === "personal") p.set("scope", draftScope);
                setSearchParams(p);
              }}
            >
              {t("dashboardUi.expenses.filters.apply")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              onClick={() => {
                setDraftQ("");
                setDraftCategory("");
                setDraftPaid("");
                setDraftScope("");
                setSearchParams(new URLSearchParams());
              }}
            >
              {t("dashboardUi.expenses.filters.clear")}
            </Button>
          </div>
        </div>
      </section>

      {expensesQuery.isLoading ? (
        <div className="mt-8">
          <LoadingState />
        </div>
      ) : expensesQuery.isError ? (
        <div className="mt-8">
          <ErrorState onRetry={() => void expensesQuery.refetch()} />
        </div>
      ) : (expensesQuery.data ?? []).length === 0 ? (
        <div className="mt-8 rounded-2xl bg-surface-container-low px-6 py-12 text-center outline outline-1 outline-outline-variant/15">
          <p className="font-body text-body-md text-on-surface-variant">{t("dashboardUi.states.emptyHint")}</p>
          <Button className="mt-4" asChild>
            <Link to={dashboardPaths.houseExpenseNew(houseId)}>{t("dashboardUi.expenses.newCta")}</Link>
          </Button>
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-8">
          {monthGroups.map((group) => {
            const headingId = `ledger-month-${group.yearMonth}`;
            const defaultCurrency = group.items[0]?.currency ?? "EUR";
            const paidTotalLine =
              group.totalsByCurrency.length > 0
                ? group.totalsByCurrency
                    .map((line) => formatMinorAsCurrency(line.amountMinor, line.currency, locale))
                    .join(" · ")
                : formatMinorAsCurrency(0, defaultCurrency, locale);
            const forecastLine =
              group.forecastTotalsByCurrency.length > 0
                ? group.forecastTotalsByCurrency
                    .map((line) => formatMinorAsCurrency(line.amountMinor, line.currency, locale))
                    .join(" · ")
                : paidTotalLine;

            return (
              <section key={group.yearMonth} aria-labelledby={headingId} className="flex flex-col gap-3">
                <div className="overflow-hidden rounded-2xl border border-outline-variant/20 bg-gradient-to-br from-surface-container-high to-surface-container-low p-5 shadow-sm">
                  <h2
                    id={headingId}
                    className="font-headline text-title-md font-bold tracking-tight text-on-surface"
                  >
                    {formatYearMonthTitle(group.yearMonth, locale)}
                  </h2>
                  <div className="mt-3 flex flex-col gap-2">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <span className="text-label-md font-medium text-on-surface-variant">
                        {t("dashboardUi.expenses.ledgerMonthTotalLabel")}
                      </span>
                      <span className="font-headline text-xl font-bold text-primary">{paidTotalLine}</span>
                    </div>
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <span className="text-label-md font-medium text-on-surface-variant">
                        {t("dashboardUi.expenses.ledgerMonthForecastTotalLabel")}
                      </span>
                      <span className="font-headline text-lg font-bold text-on-surface">{forecastLine}</span>
                    </div>
                  </div>
                  <div className="mt-4 border-t border-outline-variant/15 pt-4">
                    <h3 className="text-label-md font-semibold text-on-surface">
                      {t("dashboardUi.expenses.ledgerMemberSharesTitle")}
                    </h3>
                    {group.memberShares.length > 0 ? (
                      <ul className="mt-2 space-y-2">
                        {group.memberShares.map((m) => (
                          <li
                            key={m.userId}
                            className="flex flex-wrap items-baseline justify-between gap-2 rounded-xl bg-surface/80 px-3 py-2 text-body-md outline outline-1 outline-outline-variant/10"
                          >
                            <span className="font-medium text-on-surface">{m.name}</span>
                            <span className="font-headline font-semibold text-primary tabular-nums">
                              {m.byCurrency
                                .map((line) =>
                                  formatMinorAsCurrency(line.amountMinor, line.currency, locale),
                                )
                                .join(" · ")}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-2 text-label-md leading-relaxed text-on-surface-variant">
                        {t("dashboardUi.expenses.ledgerNoSplitsInMonth")}
                      </p>
                    )}
                  </div>
                  <div className="mt-4 border-t border-outline-variant/15 pt-4">
                    <h3 className="text-label-md font-semibold text-on-surface">
                      {t("dashboardUi.expenses.ledgerMemberSharesForecastTitle")}
                    </h3>
                    {group.forecastMemberShares.length > 0 ? (
                      <ul className="mt-2 space-y-2">
                        {group.forecastMemberShares.map((m) => (
                          <li
                            key={m.userId}
                            className="flex flex-wrap items-baseline justify-between gap-2 rounded-xl bg-surface/80 px-3 py-2 text-body-md outline outline-1 outline-outline-variant/10"
                          >
                            <span className="font-medium text-on-surface">{m.name}</span>
                            <span className="font-headline font-semibold text-primary tabular-nums">
                              {m.byCurrency
                                .map((line) =>
                                  formatMinorAsCurrency(line.amountMinor, line.currency, locale),
                                )
                                .join(" · ")}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-2 text-label-md leading-relaxed text-on-surface-variant">
                        {t("dashboardUi.expenses.ledgerNoSplitsInMonth")}
                      </p>
                    )}
                  </div>
                </div>
                <ul className="flex flex-col gap-2">
                  {group.items.map((e, index) => {
                    const eff = expenseEffectiveMinor(e);
                    const showForecastHint =
                      e.actualAmountMinor != null && e.actualAmountMinor !== e.amountMinor;
                    return (
                      <motion.li
                        key={e.id}
                        initial={prefersReducedMotion ? undefined : { opacity: 0, y: 8 }}
                        animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
                        transition={
                          prefersReducedMotion
                            ? undefined
                            : {
                                duration: 0.2,
                                delay: Math.min(index, 7) * 0.035,
                                ease: [0.22, 1, 0.36, 1],
                              }
                        }
                      >
                        <div className="flex flex-col gap-2 rounded-2xl bg-surface-container-low px-4 py-3 outline outline-1 outline-outline-variant/15 sm:flex-row sm:items-stretch sm:gap-3 sm:px-5 sm:py-4">
                          <Link
                            to={dashboardPaths.houseExpenseDetail(houseId, e.id)}
                            className="min-w-0 flex flex-1 flex-col gap-1 rounded-xl py-1 transition-colors hover:bg-surface-container-lowest/80 sm:px-2"
                          >
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                              <span className="font-headline font-bold text-on-surface">{e.title}</span>
                              <span className="font-headline text-sm font-bold text-primary">
                                {formatMinorAsCurrency(eff, e.currency, locale)}
                              </span>
                            </div>
                            {showForecastHint ? (
                              <p className="text-label-md text-on-surface-variant">
                                {t("dashboardUi.expenses.listForecastVsActual", {
                                  amount: formatMinorAsCurrency(e.amountMinor, e.currency, locale),
                                })}
                              </p>
                            ) : null}
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-label-md text-on-surface-variant">
                              <span>{formatShort(e.occurredOn)}</span>
                              {e.category?.name ? (
                                <span>
                                  {t("dashboardUi.expenses.category")}: {e.category.name}
                                </span>
                              ) : null}
                              <span>
                                {e.paidAt ? t("dashboardUi.expenses.paid") : t("dashboardUi.expenses.unpaid")}
                              </span>
                              {e.paidFromJointAccount === true ? (
                                <span>
                                  {t("dashboardUi.expenses.paidBy")}:{" "}
                                  {t("dashboardUi.expenses.form.paidByJointOption")}
                                </span>
                              ) : e.paidBy ? (
                                <span>
                                  {t("dashboardUi.expenses.paidBy")}: {e.paidBy.name}
                                </span>
                              ) : null}
                              {e.contributionScope === "shared" ? (
                                <span>{t("dashboardUi.expenses.scopeShared")}</span>
                              ) : e.contributionScope === "personal" ? (
                                <span>{t("dashboardUi.expenses.scopePersonal")}</span>
                              ) : null}
                            </div>
                          </Link>
                          <div className="flex shrink-0 flex-row items-center justify-end gap-2 sm:flex-col sm:justify-center">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="gap-1.5 rounded-xl font-label text-xs"
                              onClick={() => setActualModalExpense(e)}
                            >
                              <Icon name="Banknote" className="size-4 shrink-0" strokeWidth={2} aria-hidden />
                              <span className="max-sm:sr-only">{t("dashboardUi.expenses.actionSetActualAmount")}</span>
                            </Button>
                            {e.paidAt ? null : (
                              <>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="gap-1.5 rounded-xl bg-secondary font-label text-xs text-white hover:bg-secondary/90"
                                  onClick={() => setConfirmMarkPaidId(e.id)}
                                >
                                  <Icon name="Check" className="size-4 shrink-0" strokeWidth={2} aria-hidden />
                                  <span className="max-sm:sr-only">{t("dashboardUi.expenses.actionMarkPaid")}</span>
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={paidZeroMutation.isPending}
                                  className="gap-1.5 rounded-xl border-error/40 font-label text-xs text-error hover:bg-error-container/20"
                                  onClick={() => setConfirmPaidZeroId(e.id)}
                                >
                                  <Icon name="CircleSlash" className="size-4 shrink-0" strokeWidth={2} aria-hidden />
                                  <span className="max-sm:sr-only">{t("dashboardUi.expenses.actionPaidZero")}</span>
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </motion.li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      {confirmPaidZeroId && confirmPaidZeroExpense ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="presentation"
          onClick={() => !paidZeroMutation.isPending && setConfirmPaidZeroId(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="paid-zero-dialog-title"
            className="max-w-md rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-xl"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h2 id="paid-zero-dialog-title" className="font-headline text-lg font-bold text-error">
              {t("dashboardUi.expenses.paidZeroConfirmTitle")}
            </h2>
            <p className="mt-2 font-body text-body-md text-on-surface-variant">
              {t("dashboardUi.expenses.paidZeroConfirmBody")}
            </p>
            <p className="mt-3 font-headline text-sm font-semibold text-on-surface">
              {confirmPaidZeroExpense.title} ·{" "}
              {formatMinorAsCurrency(
                expenseEffectiveMinor(confirmPaidZeroExpense),
                confirmPaidZeroExpense.currency,
                locale,
              )}
            </p>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                disabled={paidZeroMutation.isPending}
                onClick={() => setConfirmPaidZeroId(null)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="rounded-xl"
                disabled={paidZeroMutation.isPending}
                onClick={() => void paidZeroMutation.mutate(confirmPaidZeroId)}
              >
                {paidZeroMutation.isPending
                  ? t("dashboardUi.expenses.form.saving")
                  : t("dashboardUi.expenses.paidZeroConfirmCta")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmMarkPaidId && confirmExpense ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="presentation"
          onClick={() => setConfirmMarkPaidId(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="mark-paid-dialog-title"
            className="max-w-md rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-xl"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h2 id="mark-paid-dialog-title" className="font-headline text-lg font-bold text-primary">
              {t("dashboardUi.expenses.markPaidConfirmTitle")}
            </h2>
            <p className="mt-2 font-body text-body-md text-on-surface-variant">
              {t("dashboardUi.expenses.markPaidConfirmBody")}
            </p>
            <p className="mt-3 font-headline text-sm font-semibold text-on-surface">
              {confirmExpense.title} ·{" "}
              {formatMinorAsCurrency(expenseEffectiveMinor(confirmExpense), confirmExpense.currency, locale)}
            </p>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => setConfirmMarkPaidId(null)}>
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                className="rounded-xl"
                disabled={markPaidMutation.isPending}
                onClick={() => void markPaidMutation.mutate(confirmMarkPaidId)}
              >
                {markPaidMutation.isPending ? t("dashboardUi.expenses.form.saving") : t("dashboardUi.expenses.markPaidConfirmCta")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {actualModalExpense ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="presentation"
          onClick={() => !patchActualMutation.isPending && setActualModalExpense(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="actual-amount-dialog-title"
            className="max-w-md rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-xl"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h2 id="actual-amount-dialog-title" className="font-headline text-lg font-bold text-primary">
              {t("dashboardUi.expenses.actualAmountModalTitle")}
            </h2>
            <p className="mt-2 font-body text-body-md text-on-surface-variant">
              {t("dashboardUi.expenses.actualAmountModalHint")}
            </p>
            <p className="mt-2 text-label-md text-on-surface">
              {actualModalExpense.title} —{" "}
              {t("dashboardUi.expenses.form.amountLabel")}:{" "}
              {formatMinorAsCurrency(actualModalExpense.amountMinor, actualModalExpense.currency, locale)}
            </p>
            <label className="mt-4 block font-body text-body-md text-on-surface-variant">
              <span className="text-label-md">{t("dashboardUi.expenses.actualAmountLabel")}</span>
              <input
                type="text"
                inputMode="decimal"
                value={actualDraft}
                onChange={(ev) => setActualDraft(ev.target.value)}
                className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 py-2 text-on-surface"
                autoComplete="off"
              />
            </label>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                disabled={patchActualMutation.isPending}
                onClick={() =>
                  void patchActualMutation.mutate({
                    expenseId: actualModalExpense.id,
                    actualAmountMinor: null,
                  })
                }
              >
                {t("dashboardUi.expenses.actualAmountClear")}
              </Button>
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => setActualModalExpense(null)}>
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                className="rounded-xl"
                disabled={patchActualMutation.isPending}
                onClick={() => {
                  const trimmed = actualDraft.trim();
                  if (trimmed === "") {
                    void patchActualMutation.mutate({
                      expenseId: actualModalExpense.id,
                      actualAmountMinor: null,
                    });
                    return;
                  }
                  const minor = parseAmountToMinor(actualDraft);
                  if (minor == null || minor < 0) return;
                  void patchActualMutation.mutate({
                    expenseId: actualModalExpense.id,
                    actualAmountMinor: minor,
                  });
                }}
              >
                {patchActualMutation.isPending ? t("dashboardUi.expenses.form.saving") : t("dashboardUi.expenses.actualAmountSubmit")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
